import { Component } from 'react';
import { Box } from '@mui/material';
import { appContext } from '../../../AppContext';
import Helper from '../../../Common/Helper';
import { DateHelper } from '../../../Common/DateHelper';
import { DateTime } from 'luxon';
import IbssDialog from '../BaseDialog/IbssDialog';
import { Typography } from '@mui/material';
import IbssButton from '../../Buttons/Button/IbssButton';
import { PagedResponse } from '../../../Providers.Api/Models';
import { ICateringOrder, ICateringMenuExpanded, ICostCodeAllocation, ICreateCateringOrder, IMenuItem, ISelectCateringOrder } from './DataModels';
import { OverridableStringUnion } from '@mui/types';
import { ButtonPropsColorOverrides, ButtonPropsVariantOverrides } from '@mui/material';
import { CateringOrderStatus } from '../../../Common/Enums';

class ChangeBookingDetailsDialog extends Component<IProps, IState>
{
    private get apiClient() { return appContext().apiClient; }
    private helper = new Helper();
    private get labels(){ return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    
    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
            message: '',
            scenario: '',
        };
    }

    public componentDidMount(): void
    {
        this.manageChangeBookingDetailsModal();
    }

    public componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>): void 
    {
        if(this.props.bookingStart !== prevProps.bookingStart && this.props.bookingStart !== this.props.originalBookingStart)
        {
            this.manageChangeBookingDetailsModal();
            return;
        }

        if (this.props.bookingEnd !== prevProps.bookingEnd && this.props.bookingEnd !== this.props.originalBookingEnd)
        {
            this.manageChangeBookingDetailsModal();
            return;
        }

        if(this.props.isSwappingSpace !== prevProps.isSwappingSpace && this.props.isSwappingSpace)
        {
            this.manageChangeBookingDetailsModal();
            return;
        }
}

    private get cateringCutOffTime(): DateTime
    {
        const rootNode = this.local.getNodeData();
        const building = this.helper.getBuildingById(rootNode, this.props.buildingId);
        const cateringCutOffTimeRaw = building?.Pre_Book_Cat_Time ?? '';
        const newBookingStart = DateTime.fromJSDate(this.props.bookingStart).setZoneByNode(this.props.buildingId);
        const cutOffTimeDays = cateringCutOffTimeRaw.split('.')[0];
        const cutOffTimeOfDay = cateringCutOffTimeRaw.split('.')[1]?.split(':');
        const cateringCutOffTime = cutOffTimeOfDay && newBookingStart.date().plus({ days: -cutOffTimeDays }).set({ hour: parseInt(cutOffTimeOfDay[0]), minute: parseInt(cutOffTimeOfDay[1]) });
        return cateringCutOffTime
    }

    private manageChangeBookingDetailsModal(): void
    {
        const newBookingStart = DateTime.fromJSDate(this.props.bookingStart).setZoneByNode(this.props.buildingId);
        const oldBookingStart = DateTime.fromJSDate(this.props.originalBookingStart).setZoneByNode(this.props.buildingId);

        const newBookingEnd = DateTime.fromJSDate(this.props.bookingEnd).setZoneByNode(this.props.buildingId);
        const oldBookingEnd = DateTime.fromJSDate(this.props.originalBookingEnd).setZoneByNode(this.props.buildingId);

        const cateringCutOffTime = this.cateringCutOffTime;
        const buildingNow = DateTime.now().setZoneByNode(this.props.buildingId);

        if(newBookingStart === oldBookingStart && this.props.spaceId === this.props.originalSpaceId)
        {
            // if starting time has not changed AND spaceId has not changed, return;
            return;
        }
        
        const { displayLocation } = this.props;

        if((buildingNow < cateringCutOffTime))
        {
            if(this.props.isSwappingSpace)
            {
                // Scenario 4. Changing SpaceId, Pre-Cut Off;
                const scenario = `${displayLocation}.Space.PreCateringCutOff`;
                this.setState({ message: this.getWarningMessage(scenario),  scenario: scenario });
                return;
            }

            if(!newBookingStart.hasSame(oldBookingStart, 'minute') || !newBookingEnd.hasSame(oldBookingEnd, 'minute'))
            {
                // Scenario 1. Booking Time change, Pre-Cut Off;
                const scenario = `${displayLocation}.BookingTime.PreCateringCutOff`;
                this.setState({ message: this.getWarningMessage(scenario),  scenario: scenario });
            }
            else if(!newBookingStart.hasSame(oldBookingStart, 'day'))
            {
                // Scenario 2. Booking Date change, Pre-Cut Off
                const scenario = `${displayLocation}.BookingDate.PreCateringCutOff`;
                this.setState({ message: this.getWarningMessage(scenario),  scenario: scenario });
            }
        }
        else if((buildingNow >= cateringCutOffTime))
        {   
            if(this.props.isSwappingSpace)
            {
                // Scenario 4. Changing SpaceId, Post-Cut Off;
                const scenario = `${displayLocation}.Space.PostCateringCutOff`;
                this.setState({ message: this.getWarningMessage(scenario),  scenario: scenario });
                return;
            }

            if(!newBookingStart.hasSame(oldBookingStart, 'minute') || !newBookingEnd.hasSame(oldBookingEnd, 'minute'))
            {
                // Scenario 1. Booking Time change, Post-Cut Off
                const scenario = `${displayLocation}.BookingTime.PostCateringCutOff`;
                this.setState({ message: this.getWarningMessage(scenario), scenario: scenario });
            }
            else if(!newBookingStart.hasSame(oldBookingStart, 'day'))
            {
                // Scenario 2. Booking Date change, Post-Cut Off
                const scenario = `${displayLocation}.BookingDate.PostCateringCutOff`;
                this.setState({ message: this.getWarningMessage(scenario),  scenario: scenario });
            }
        }
        else
        {
            return;
        }
        
    }

    private async getCateringOrdersForBooking(): Promise<ISelectCateringOrder[]>
    {
        try
        {
            const cateringOrdersForBooking = await appContext().ibssApiClientV2.v2.byNodeid.cateringOrder.get<PagedResponse<ISelectCateringOrder[]>>({
                nodeId: this.props.buildingId,
                filter: `Booking_Id eq '${this.props.bookingId}'`,
                recursive: true,
                top: 20,
                select: ISelectCateringOrder,
            });
            return cateringOrdersForBooking.value;
        }
        catch
        {
            return [];
        }
    }

    private async getCateringMenus(): Promise<ICateringMenuExpanded[]>
    {
        try
        {
            const cateringMenus = await appContext().ibssApiClientV2.v2.byNodeid.cateringMenu.get<PagedResponse<ICateringMenuExpanded[]>>
            ({
                nodeId: this.props.buildingId,
                recursive: true,
                top: 100,
                select: ICateringMenuExpanded,
                filter: `Status eq 'StatusActive' and Available_From le ${DateTime.fromJSDate(this.props.bookingStart).toUtcByNode(this.props.buildingId).toISO()} and Available_Until ge ${DateTime.fromJSDate(this.props.bookingEnd).toUtcByNode(this.props.buildingId).toISO()} and Menu_Spaces/any(Menu_Space: Menu_Space/Space_Id eq '${this.props.spaceId}')`
            });

            return cateringMenus.value;
        }
        catch
        {
            return [];
        }
    }

    private async handleSwapSpaceCateringOrders(): Promise<void>
    {
        const cateringOrders = await this.getCateringOrdersForBooking();
        const cateringOrdersView = cateringOrders.map(order => OrderView.fromApiTask({ ...order, Menu_Items: JSON.parse(order.Menu_Items) }));

        const menus = await this.getCateringMenus();
        // list of menus in new space
        const menusView = menus.map(menu=> MenuView.fromApiModel(menu));

        // orders with menus that can'be be matched to menus on new space - cancel
        const cateringOrderNoActiveMenus = cateringOrdersView.filter(order => !menusView.find(menu => menu.menu.menuId === order.menuItems[0].menuId));
         // other orders - patch call to change spaceId, set to status to isPending,
        const cateringOrderWithActiveMenus = cateringOrdersView.filter(order => !cateringOrderNoActiveMenus.find(cateringOrder => cateringOrder.orderId === order.orderId));

        await this.patchSwapSpaceCateringOrders(cateringOrderWithActiveMenus.map(order => order.orderId));
        cateringOrderNoActiveMenus.forEach(order =>
            {
                appContext().ibssApiClientV2.v2.byNodeid.cateringOrder.byOrderId.patch({
                    nodeId: this.props.nodeId,
                    orderId: order.orderId,
                    body: {
                        Catering_Order_Status: CateringOrderStatus.CancellationPending,
                        Space_Id: this.props.spaceId,
                    }
                });
            });
    }

    private bookingTimeScopeChange(): boolean
    {
        const bookingStartDateTime = DateTime.fromJSDate(this.props.bookingStart);
        const bookingEndDateTime = DateTime.fromJSDate(this.props.bookingEnd);

        const originalBookingStartDateTime = DateTime.fromJSDate(this.props.originalBookingStart);
        const originalBookingEndDateTime = DateTime.fromJSDate(this.props.originalBookingEnd);

        const originalStartTimeInMinutes = originalBookingStartDateTime.hour * 60 + originalBookingStartDateTime.minute;
        const newStartTimeInMinutes = bookingStartDateTime.hour * 60 + bookingStartDateTime.minute;
        const originalEndTimeInMinutes = originalBookingEndDateTime.hour * 60 + originalBookingEndDateTime.minute;
        const newEndTimeInMinutes = bookingEndDateTime.hour * 60 + bookingEndDateTime.minute;

        return (newStartTimeInMinutes > originalStartTimeInMinutes) || (newEndTimeInMinutes < originalEndTimeInMinutes)
    }

    private async handleDateTimeChangeCateringOrders(status?: string): Promise<void>
    {
        const bookingStartDateTime = DateTime.fromJSDate(this.props.bookingStart);
        const bookingEndDateTime = DateTime.fromJSDate(this.props.bookingEnd);
        const originalBookingStartDateTime = DateTime.fromJSDate(this.props.originalBookingStart);

        let cateringOrdersWithDateTimeUpdated = [] as ICateringDateTimePatch[];


        if (this.bookingTimeScopeChange())
        {
            const cateringOrders = await this.getCateringOrdersForBooking();
            cateringOrdersWithDateTimeUpdated = cateringOrders.map(order =>
            {
                {
                    const newServiceTime = bookingStartDateTime.toUtcByNode(this.props.nodeId).toISO();
                    const newClearingTime = bookingEndDateTime.toUtcByNode(this.props.nodeId).toISO();
                    return ({ orderId: order.Order_Id, serviceTime: newServiceTime, clearingTime: newClearingTime, status: order.Catering_Order_Status })
                }
            });

            cateringOrdersWithDateTimeUpdated.forEach(order =>
            {
                appContext().ibssApiClientV2.v2.byNodeid.cateringOrder.byOrderId.patch({
                    nodeId: this.props.nodeId,
                    orderId: order.orderId,
                    body: {
                        Catering_Service_Time: order.serviceTime,
                        Catering_Clearing_Time: order.clearingTime,
                        Catering_Order_Status: status ? status : order.status
                    }
                });
            });
        }
        else if (!bookingStartDateTime.hasSame(originalBookingStartDateTime, 'day'))
        {
            const cateringOrders = await this.getCateringOrdersForBooking();
            cateringOrdersWithDateTimeUpdated = cateringOrders.map(order =>
            {
                {
                    const newServiceTime = DateHelper.fromIsoByNode(order.Catering_Service_Time, this.props.nodeId).set({
                        day: bookingStartDateTime.day,
                        month: bookingStartDateTime.month,
                        year: bookingStartDateTime.year
                    }).toUtcByNode(this.props.nodeId).toISO();

                    const newClearingTime = DateHelper.fromIsoByNode(order.Catering_Clearing_Time, this.props.nodeId).set({
                        day: bookingStartDateTime.day,
                        month: bookingStartDateTime.month,
                        year: bookingStartDateTime.year
                    }).toUtcByNode(this.props.nodeId).toISO();
                    return ({ orderId: order.Order_Id, serviceTime: newServiceTime, clearingTime: newClearingTime, status: order.Catering_Order_Status })
                }
            });

            cateringOrdersWithDateTimeUpdated.forEach(order =>
            {
                appContext().ibssApiClientV2.v2.byNodeid.cateringOrder.byOrderId.patch({
                    nodeId: this.props.nodeId,
                    orderId: order.orderId,
                    body: {
                        Catering_Service_Time: order.serviceTime,
                        Catering_Clearing_Time: order.clearingTime,
                        Catering_Order_Status: status ? status : order.status
                    }
                });
            });
        }
        else
        {
            if (status)
            {
                const cateringOrders = await this.getCateringOrdersForBooking();
                cateringOrders.forEach(order =>
                {
                    appContext().ibssApiClientV2.v2.byNodeid.cateringOrder.byOrderId.patch({
                        nodeId: this.props.nodeId,
                        orderId: order.Order_Id,
                        body: {
                            Catering_Order_Status: status
                        }
                    });
                });
            }
        }
    }

    private async patchSwapSpaceCateringOrders(cateringOrderIds: string[]): Promise<void>
    {
        if(cateringOrderIds.length === 0)
        {
            return;
        }

        await this.patchSwapSpaceCateringOrder(cateringOrderIds);
    }

    private async patchSwapSpaceCateringOrder(cateringOrderIds: string[]): Promise<void>
    {
        if (!this.props.nodeId)
        {
            return;
        }

        const cateringPatchPayload =
        {
            Catering_Order_Status: this.props.alwaysApproveCateringOrders ? CateringOrderStatus.Approved : CateringOrderStatus.PendingApproval,
            Space_Id: this.props.spaceId,
        };

        if (cateringOrderIds.length == 0)
        {
            return;
        }

        try
        {
            cateringOrderIds.forEach(orderId =>
            {
                this.apiClient.cateringOrders.patchCateringOrder(this.props.nodeId, orderId, cateringPatchPayload);
            });
        }
        catch
        {
            return;
        }
    }

    private renderButton(
        variant: OverridableStringUnion<"text" | "outlined" | "contained", ButtonPropsVariantOverrides> | undefined, 
        color: OverridableStringUnion<"inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning", ButtonPropsColorOverrides> | undefined, 
        label: string
    ): JSX.Element
    {
        return (
            <IbssButton
                variant={variant}
                color={color}
                onClick={() => this.proceedWithUpdate()}
            >
                {label}
            </IbssButton>
        )
    }

    private get proceedButton(): JSX.Element
    {
        switch(this.state.scenario)
        {
            case 'FLEX.BookingTime.PreCateringCutOff':
            case 'FLEX.BookingDate.PreCateringCutOff':
            case 'ONELENS.BookingTime.PreCateringCutOff':
            case 'ONELENS.BookingTime.PostCateringCutOff':
            case 'ONELENS.BookingDate.PreCateringCutOff':
                return this.renderButton('contained', 'primary', this.labels.funcBookingsYesProceed_S);
            
            case 'FLEX.BookingTime.PostCateringCutOff':
            case 'FLEX.BookingDate.PostCateringCutOff':
            case 'ONELENS.BookingDate.PostCateringCutOff':
            case 'ONELENS.Space.PreCateringCutOff':
            case 'ONELENS.Space.PostCateringCutOff':
            case 'FLEX.Space.PreCateringCutOff':
            case 'FLEX.Space.PostCateringCutOff':
                return this.renderButton('contained', 'error', this.labels.funcBookingsYesProceed_S);

            default:
                return (
                    <></>
                ) 
        }
    }

    private getWarningMessage(scenario: string): string
    {
        switch (scenario)
        {
            case 'FLEX.BookingTime.PreCateringCutOff':
                if (this.props.alwaysApproveCateringOrders == 1 && this.bookingTimeScopeChange())
                {
                    return this.labels.funcCateringPreCutOffWithChange;
                }
                else if (this.props.alwaysApproveCateringOrders == 1 && !this.bookingTimeScopeChange())
                {
                    return this.labels.funcCateringPreCutOffNoChange
                }
                else if (this.props.alwaysApproveCateringOrders == 0 && this.bookingTimeScopeChange())
                {
                    return this.labels.funcCateringPreCutOffWithChange
                }
                else
                {
                    return this.labels.funcCateringPreCutOffNoChange
                }

            case 'FLEX.BookingTime.PostCateringCutOff':
                if (this.props.alwaysApproveCateringOrders == 1 && this.bookingTimeScopeChange())
                {
                    return this.labels.funcCateringPostCutOffWithChange;
                }
                else if (this.props.alwaysApproveCateringOrders == 1 && !this.bookingTimeScopeChange())
                {
                    return this.labels.funcCateringPostCutOffNoChange
                }
                else
                {
                    return this.labels.funcCateringPostCutOffWithChangeState
                }

            case 'FLEX.BookingDate.PreCateringCutOff':
                return this.labels.funcCateringPreCutOffNoChange;

            case 'FLEX.BookingDate.PostCateringCutOff':
                return this.props.alwaysApproveCateringOrders == 1 ? this.labels.funcCateringPostCutOffNoChange : this.labels.funcCateringPostCutOffWithChangeState;

            case 'FLEX.Space.PreCateringCutOff':
                return this.labels.funcCateringChangeSpaceNoMenu;

            case 'FLEX.Space.PostCateringCutOff':
                return this.labels.funcCateringChangeSpaceNoMenu;

            case 'ONELENS.BookingTime.PreCateringCutOff':
                if (this.props.alwaysApproveCateringOrders == 1 && this.bookingTimeScopeChange())
                {
                    return this.labels.funcCateringPreCutOffWithChange;
                }
                else if (this.props.alwaysApproveCateringOrders == 1 && !this.bookingTimeScopeChange())
                {
                    return this.labels.funcCateringPreCutOffNoChange
                }
                else
                {
                    return this.labels.funcCateringPreCutOffWithChangeState
                }

            case 'ONELENS.BookingTime.PostCateringCutOff':
                if (this.props.alwaysApproveCateringOrders == 1 && this.bookingTimeScopeChange())
                {
                    return this.labels.funcCateringPostCutOffWithChange;
                }
                else if (this.props.alwaysApproveCateringOrders == 1 && !this.bookingTimeScopeChange())
                {
                    return this.labels.funcCateringPostCutOffNoChange
                }
                else
                {
                    return this.labels.funcCateringPostCutOffWithChangeState
                }

            case 'ONELENS.BookingDate.PreCateringCutOff':
                return this.props.alwaysApproveCateringOrders == 1 ? this.labels.funcCateringPreCutOffNoChange : this.labels.funcCateringPreCutOffWithChangeState;

            case 'ONELENS.BookingDate.PostCateringCutOff':
                return this.props.alwaysApproveCateringOrders == 1 ? this.labels.funcCateringPostCutOffNoChange : this.labels.funcCateringPostCutOffWithChangeState;

            case 'ONELENS.Space.PreCateringCutOff':
                return this.labels.funcCateringChangeSpaceNoMenu;

            case 'ONELENS.Space.PostCateringCutOff':
                return this.labels.funcCateringChangeSpaceNoMenu;

            default:
                return '';
        }
    }

    private getCateringUpdateFn(scenario: string): ()=> Promise<void>
    {

        switch(scenario)
        {
            case 'FLEX.BookingTime.PreCateringCutOff':
                return ()=> this.props.alwaysApproveCateringOrders == 1 ? this.handleDateTimeChangeCateringOrders() : this.handleDateTimeChangeCateringOrders(CateringOrderStatus.PendingApproval);

            case 'FLEX.BookingTime.PostCateringCutOff':
                return ()=> this.props.alwaysApproveCateringOrders == 1 ? this.handleDateTimeChangeCateringOrders() : this.handleDateTimeChangeCateringOrders(CateringOrderStatus.PendingApproval);

            case 'FLEX.BookingDate.PreCateringCutOff':
                return ()=> this.props.alwaysApproveCateringOrders == 1 ? this.handleDateTimeChangeCateringOrders() : this.handleDateTimeChangeCateringOrders(CateringOrderStatus.PendingApproval);

            case 'FLEX.BookingDate.PostCateringCutOff':
                return ()=> this.props.alwaysApproveCateringOrders == 1 ? this.handleDateTimeChangeCateringOrders() : this.handleDateTimeChangeCateringOrders(CateringOrderStatus.PendingApproval);
            
            case 'FLEX.Space.PreCateringCutOff':
            return ()=> this.handleSwapSpaceCateringOrders();

            case 'FLEX.Space.PostCateringCutOff':
                return ()=> this.handleSwapSpaceCateringOrders();

            case 'ONELENS.BookingTime.PreCateringCutOff':
                return ()=> this.props.alwaysApproveCateringOrders == 1 ? this.handleDateTimeChangeCateringOrders() : this.handleDateTimeChangeCateringOrders(CateringOrderStatus.PendingApproval);

            case 'ONELENS.BookingTime.PostCateringCutOff':
                return ()=> this.props.alwaysApproveCateringOrders == 1 ? this.handleDateTimeChangeCateringOrders() : this.handleDateTimeChangeCateringOrders(CateringOrderStatus.PendingApproval);

            case 'ONELENS.BookingDate.PreCateringCutOff':
                return ()=> this.props.alwaysApproveCateringOrders == 1 ? this.handleDateTimeChangeCateringOrders() : this.handleDateTimeChangeCateringOrders(CateringOrderStatus.PendingApproval);

            case 'ONELENS.BookingDate.PostCateringCutOff':
                return ()=> this.props.alwaysApproveCateringOrders == 1 ? this.handleDateTimeChangeCateringOrders() : this.handleDateTimeChangeCateringOrders(CateringOrderStatus.PendingApproval);
            
            case 'ONELENS.Space.PreCateringCutOff':
                return ()=> this.handleSwapSpaceCateringOrders();

            case 'ONELENS.Space.PostCateringCutOff':
                return ()=> this.handleSwapSpaceCateringOrders();

            default:
                return ()=> Promise.resolve();
        }
    }

    private async proceedWithUpdate(): Promise<void>
    {
        const updateCatering = this.getCateringUpdateFn(this.state.scenario);
        try
        {
            await this.props.updateBooking();
        } catch (error)
        {
            this.props.onClose();
            return;
        }
        await updateCatering();
        this.props.onClose();
    }

    public render(): JSX.Element
    {
        return (
            <IbssDialog
                aria-modal="true"
                aria-label="change booking details warning modal"
                fullWidth
                maxWidth={'md'}
                open={this.props.show}
                onClose={() => this.props.onClose()}
                header=
                {
                    <>
                        <label className="modal-heading">{this.labels.HubLabelWarning}</label>
                    </>
                }
                dialogContent=
                {
                    <Box>
                        <Typography>{this.state.message}</Typography>
                    </Box>
                }
                footer=
                {
                    <div className='d-flex w-90' style={{ justifyContent: 'center', gap: '16px'}}>
                        <IbssButton
                            variant="contained"
                            color='secondary'
                            onClick={()=> this.props.onClose()}
                        >
                            {this.labels.funcBookingScheduleCancelNo_S}
                        </IbssButton>
                        {this.proceedButton}
                    </div>
                }
            />
        )
    }
}

export default ChangeBookingDetailsDialog;

export interface IProps
{
    show: boolean;
    isSwappingSpace: boolean;
    displayLocation: IBookingDetailsApplication;
    onClose: ()=> void;
    buildingId: number;
    nodeId: number;
    spaceId: string;
    updateBooking: ()=> Promise<void>;
    bookingStart: Date;
    bookingEnd: Date;
    originalBookingStart: Date;
    originalBookingEnd: Date;
    originalSpaceId: string;
    cateringOrderIds?: string[];
    bookingId?: string;
    alwaysApproveCateringOrders: number;
}

export interface IState
{
    message: string;
    scenario: string;
}

enum ICateringCutOffStatus
{
    PreCutOff='PreCutOff',
    PostCutOff='PostCutOff'
}

export enum IBookingDetailsApplication
{
    Flex='FLEX',
    Onelens='ONELENS',
}


export class MenuView
{
    public menu = {
        menuId: '',
        name: ''
    };

    public static fromApiModel(cateringMenu: ICateringMenuExpanded): MenuView
    {
        return {
            menu: {
                menuId: cateringMenu.Menu_Id,
                name: cateringMenu.Name,
            }
        };
    }
}

export class OrderView
{
    public orderId = "";
    public nodeId = 0;
    public menuItems = new Array<MenuItemView>();
    public cateringServiceTime = DateHelper.null();
    public cateringClearingTime = DateHelper.null();
    public cateringStatus = "";
    public cateringAttendees = "";
    public cateringNotes = "";
    public costCodeAllocation: ICostAllocation[] = [];
    public cateringOrderRestrictions = [];
    public bookingName = "";
    public bookingId = "";
    public spaceName = "";
    public spaceId = "";
    public menuId = "";

    constructor(value?: Partial<OrderView>)
    {
        if (value == null)
        {
            return;
        }
        Object.assign(this, value);
    }

    public static fromApiTask(data: ICateringOrder): OrderView
    {
        return new OrderView(
            {
                orderId: data.Order_Id,
                nodeId: data.Node_Id,
                menuItems: data.Menu_Items.map(i => MenuItemView.fromApiModel(i)),
                cateringServiceTime: DateHelper.fromIsoByNode(data.Catering_Service_Time, data.Node_Id),
                cateringClearingTime: DateHelper.fromIsoByNode(data.Catering_Clearing_Time, data.Node_Id),
                cateringStatus: data.Catering_Order_Status,
                cateringAttendees: data.Catering_Attendees,
                cateringNotes: data.Catering_Notes,
                costCodeAllocation: JSON.parse(data.Cost_Code_Allocation).map((x: { Cost_Code: string; Cost_Code_Id: string; Allocation: number }) => ({ costCode: x.Cost_Code, costCodeId: x.Cost_Code_Id, allocation: x.Allocation })),
                cateringOrderRestrictions: data.Catering_Order_Restrictions.length > 0 ? JSON.parse(data.Catering_Order_Restrictions) : [],
                bookingName: data.Booking_Name,
                bookingId: data.Booking_Id,
                spaceName: data.Space_Name,
                spaceId: data.Space_Id,
                menuId: data.Menu_Id
            });
    }

    public toApiOrder(spaceId: string, bookingId: string, nodeId: number, numOfAttendees: number, orderItems: string, costCodeAllocations: ICostCodeAllocation[], orderTotal: number, restrictions: ICateringRestrictionView[], orderStatus: string, cateringServiceTime: string, cateringClearingTime: string): ICreateCateringOrder
    {
        const payload: ICreateCateringOrder =
        {
            Node_Id: nodeId,
            Menu_Items: orderItems,
            Booking_Id: bookingId,
            Space_Id: spaceId,
            Catering_Service_Time: cateringServiceTime,
            Catering_Clearing_Time: cateringClearingTime,
            Catering_Order_Status: orderStatus,
            Catering_Attendees: numOfAttendees,
            Catering_Notes: this.cateringNotes,
            Cost_Code_Allocation: JSON.stringify(costCodeAllocations),
            Catering_Total_Value: orderTotal,
            Catering_Order_Restrictions: JSON.stringify(restrictions)
        };

        return payload;
    }
}

export class MenuItemView
{
    public id = "";
    public menuId = "";
    public quantityOfItems = 0;

    public static fromApiModel(data: IMenuItem): MenuItemView
    {
        return {
            id: data.Id,
            menuId: data.Menu_Id,
            quantityOfItems: data.QuantityOfItems,
        };
    }
}

export interface ICateringRestrictionView
{
    id: string;
    section: string;
    imageURI: string;
    name: string;
}

export interface ICostAllocation
{
    costCode: string;
    costCodeId: string;
    allocation: number;
    costCodeDescription: string;
}

export interface ICateringDateTimePatch
{
    orderId: string;
    serviceTime: string;
    clearingTime: string;
    status: string;
}